import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, InputGroup, Alert } from "react-bootstrap";
import {
  resolveAreaFormula,
  formulaDimensions,
  computeMarineArea,
  evaluateAreaFormula,
  lineTotal,
  roundArea,
  AREA_VAR_LABELS,
  areaVarWarning,
} from "../../util/marineCalc";
import { getMarineRate, formatArea, marineAttrsOf } from "../../util/marineService";

/**
 * Area calculator for a marine service line.
 *
 * A standalone component by design — deliberately NOT derived from
 * addCustomCut. That modal captures one price for a service that has none;
 * this is a live calculator over a variable set driven by the service's own
 * area formula, with a computed-vs-override distinction.
 *
 * Inputs are driven by `formulaDimensions()` rather than a hardcoded coverage
 * map, so when the server ships richer formulas this renders them with no
 * change here.
 *
 * `hull_height_in` is null on every feed-supplied boat — no feed carries
 * freeboard — so for hull coverage the estimator is prompted for it.
 */
export const MarineCalculatorModal = ({ show, service, vehicle, onConfirm, onCancel }) => {
  // Reads either shape — nested (fresh from the wizard) or flat (a saved boat
  // re-fetched from the server, which stores the fields at the top level).
  const attrs = marineAttrsOf(vehicle);
  const rate = getMarineRate(service) ?? 0;

  // Resolve the formula once per service. A service whose basis this version
  // does not recognise fails here, visibly, rather than producing a number.
  const { formula, formulaError } = useMemo(() => {
    try {
      return { formula: resolveAreaFormula(service), formulaError: null };
    } catch (err) {
      return { formula: null, formulaError: err.message };
    }
  }, [service]);

  const dimensions = useMemo(() => formulaDimensions(formula), [formula]);

  const serviceWastePct =
    service && service.waste_pct != null ? Number(service.waste_pct) : 0;
  // Catalog default. Marine duration is WHOLE-JOB and never scaled by area —
  // a bigger hull is more film, not proportionally more days — so the estimator
  // adjusts it here rather than it being derived.
  const serviceDurationDays =
    service && Number(service.duration_days) > 0 ? Number(service.duration_days) : 1;

  const [values, setValues] = useState({});
  const [wasteInput, setWasteInput] = useState("");
  const [durationInput, setDurationInput] = useState("");
  // The single area the line is priced on. Seeded from the calculation and
  // kept in step with it until the estimator types their own number, after
  // which it is theirs and recalculation stops touching it.
  const [coverageArea, setCoverageArea] = useState("");
  const [areaTouched, setAreaTouched] = useState(false);

  // Pre-fill from the boat each time the modal opens.
  useEffect(() => {
    if (!show) return;
    const seed = {};
    dimensions.forEach((dim) => {
      const v = attrs[dim];
      seed[dim] = v === null || v === undefined ? "" : String(v);
    });
    setValues(seed);
    setWasteInput(String(serviceWastePct));
    setDurationInput(String(serviceDurationDays));
    setCoverageArea("");
    setAreaTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, service]);

  const numericValues = useMemo(() => {
    const out = {};
    Object.keys(values).forEach((k) => {
      if (values[k] !== "" && values[k] !== null) out[k] = Number(values[k]);
    });
    return out;
  }, [values]);

  // Waste is a live parameter, not a fixed property of the catalog row — the
  // estimator can dial it for this job. The service is shadowed rather than
  // mutated so computeMarineArea keeps its single waste source.
  const wastePct = useMemo(() => {
    const n = Number(wasteInput);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [wasteInput]);

  const pricingService = useMemo(
    () => ({ ...(service || {}), waste_pct: wastePct }),
    [service, wastePct]
  );

  // Live recompute. computeMarineArea throws on a missing dimension — that is
  // the normal state before the estimator fills freeboard in, so it renders as
  // guidance rather than an error.
  const { computedArea, computeError } = useMemo(() => {
    if (!formula) return { computedArea: null, computeError: formulaError };
    try {
      return { computedArea: computeMarineArea(pricingService, numericValues), computeError: null };
    } catch (err) {
      return { computedArea: null, computeError: err.message };
    }
  }, [formula, formulaError, pricingService, numericValues]);

  // Mirror the calculation into the coverage field until the estimator claims it.
  useEffect(() => {
    if (!show || areaTouched) return;
    setCoverageArea(computedArea == null ? "" : String(computedArea));
  }, [show, areaTouched, computedArea]);

  // What the formula alone yields, before factor and waste — shown so the
  // estimator can see where the number came from.
  const rawArea = useMemo(() => {
    if (!formula) return null;
    try {
      return evaluateAreaFormula(formula, numericValues);
    } catch {
      return null;
    }
  }, [formula, numericValues]);

  const durationDays = Number(durationInput);
  const durationValid = Number.isFinite(durationDays) && durationDays > 0;

  // The coverage field is the sole source of the priced area — always.
  const areaEntered = String(coverageArea).trim() !== "";
  const finalArea = areaEntered ? roundArea(Number(coverageArea)) : null;
  const overridden = finalArea != null && finalArea !== computedArea;
  const total = finalArea == null ? null : lineTotal(finalArea, rate);
  const canConfirm = finalArea != null && rate > 0 && durationValid;

  const handleValueChange = (dim, v) => setValues((prev) => ({ ...prev, [dim]: v }));

  const handleAreaChange = (v) => {
    setAreaTouched(true);
    setCoverageArea(v);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      area: finalArea,
      rate,
      total,
      overridden,
      variables: numericValues,
      wastePct,
      durationDays,
    });
  };

  const factor = service && service.area_factor != null ? Number(service.area_factor) : 1;

  return (
    <Modal show={show} onHide={onCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Calculate area — {service?.description}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {formulaError && (
          <Alert variant="warning">
            <strong>Automatic calculation unavailable.</strong>
            <div className="small mt-1">{formulaError}</div>
            <div className="small mt-2">
              Enter the area manually below to price this service.
            </div>
          </Alert>
        )}

        {!formulaError && (
          <>
            {dimensions.map((dim) => (
              <Form.Group key={dim}>
                <Form.Label>{AREA_VAR_LABELS[dim] || dim}</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={values[dim] ?? ""}
                  onChange={(e) => handleValueChange(dim, e.target.value)}
                  placeholder={
                    attrs[dim] == null ? "Not on file — measure or estimate" : undefined
                  }
                />
                {areaVarWarning(dim, values[dim]) && (
                  <Form.Text className="text-danger">
                    {areaVarWarning(dim, values[dim])}
                  </Form.Text>
                )}
              </Form.Group>
            ))}

            <Form.Group>
              <Form.Label>Waste</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  value={wasteInput}
                  onChange={(e) => setWasteInput(e.target.value)}
                />
                <InputGroup.Append>
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </Form.Group>

            <hr />

            {computeError && !areaEntered && (
              <Alert variant="warning" className="py-2">{computeError}</Alert>
            )}

            {computedArea != null && (
              <p className="mb-2">
                Calculated:{" "}
                <strong>{formatArea(computedArea)}</strong>
                {rawArea != null && (
                  <span className="text-muted small">
                    {"  ("}
                    {rawArea.toFixed(2)} sq ft
                    {factor !== 1 && ` × ${factor}`}
                    {wastePct > 0 && ` + ${wastePct}% waste`}
                    {", rounded up)"}
                  </span>
                )}
              </p>
            )}

          </>
        )}

        <Form.Group>
              <Form.Label>Coverage Area</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="1"
                  min="1"
                  value={coverageArea}
                  onChange={(e) => handleAreaChange(e.target.value)}
                  placeholder="Enter the area for this job"
                />
                <InputGroup.Append>
                  <InputGroup.Text>sq ft</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              {overridden && computedArea != null && (
                <Form.Text className="text-muted">
                  Calculated {formatArea(computedArea)}.
                </Form.Text>
              )}
        </Form.Group>

        <Form.Group>
              <Form.Label>Duration</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={durationInput}
                  onChange={(e) => setDurationInput(e.target.value)}
                  isInvalid={durationInput !== "" && !durationValid}
                />
                <InputGroup.Append>
                  <InputGroup.Text>days</InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
              <Form.Text className="text-muted">
                Books this much time on the calendar. Whole job — not per square foot.
              </Form.Text>
        </Form.Group>

            {finalArea != null && (
              <Alert variant="light" className="mt-3 mb-0 border">
                <strong>
                  {formatArea(finalArea)} @ ${rate.toFixed(2)}/sq ft = ${total.toFixed(2)}
                </strong>
              </Alert>
            )}

            {rate <= 0 && (
              <Alert variant="danger" className="mt-3 mb-0">
                This service has no $/sq ft rate configured, so it cannot be priced.
              </Alert>
            )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!canConfirm}>
          Add to estimate
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarineCalculatorModal;
